import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { useEffect, useId, useMemo, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import { useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { useInfiniteSocialMediaPosts, useUserQuery } from '../../../../queries';
import { filterCancelledSocialPosts } from '../../../../utils/socialMediaFiltering';
import { Button } from '../../../components-v2/Button';
import { Dropdown, DropdownContent, DropdownItem, DropdownSelectTrigger } from '../../../components-v2/Dropdown';
import { Select, SelectCheckboxItem, SelectContent, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import { bs } from '../../../styles/styles';
import { UserSelectModal } from '../../UserSelectModal';
import { SocialMediaReportingTableRow } from './presentation';
import { SocialMediaReportingTable } from './SocialMediaReportingTable';
import { styleSheet } from './styles';

export const allReportStatusValues = Object.values(Api.ReportPostStatus);

const isReportPostStatus = (value: string): value is Api.ReportPostStatus =>
	allReportStatusValues.includes(value as Api.ReportPostStatus);

// Unknown and repeated values both read as "no filter" downstream, which decides it by counting.
export const reportStatusOrAll = (status: string[]): Api.ReportPostStatus[] => {
	const known = [...new Set(status.filter(isReportPostStatus))];
	return known.length ? known : allReportStatusValues;
};

export const mapReportPostStatusToPostStatus = (status: Api.ReportPostStatus[]) => {
	let postStatus: Api.PostStatus[] | null = null;
	if (status.length < allReportStatusValues.length) {
		postStatus = [];
		status.forEach(s => {
			switch (s) {
				case Api.ReportPostStatus.Scheduled:
					postStatus.push(Api.PostStatus.Scheduled);
					break;
				case Api.ReportPostStatus.Cancelled:
					postStatus.push(Api.PostStatus.Cancelled);
					break;
				case Api.ReportPostStatus.Failed:
					postStatus.push(Api.PostStatus.Failed);
					break;
				case Api.ReportPostStatus.Pending:
					postStatus.push(Api.PostStatus.Pending);
					break;
				case Api.ReportPostStatus.Posted:
					postStatus = [
						...postStatus,
						Api.PostStatus.PartiallySucceeded,
						Api.PostStatus.Succeeded,
						Api.PostStatus.Started,
					];
					break;
				default:
					break;
			}
		});
	}
	return postStatus;
};

/**
 * `userId` named this report's sender filter for nine months before `sentBy` did, so links already shared
 * still carry it. Read it when `sentBy` is absent, then rewrite it so the URL stops naming the old param.
 */
export const useSenderFilter = () => {
	const [sentBy, setSentBy] = useQueryState('sentBy', parseAsString);
	const [legacyUserId, setLegacyUserId] = useQueryState('userId', parseAsString);
	useEffect(() => {
		if (legacyUserId !== null) {
			setSentBy(sentBy ?? legacyUserId);
			setLegacyUserId(null);
		}
	}, [legacyUserId, sentBy, setSentBy, setLegacyUserId]);
	return [sentBy ?? legacyUserId, setSentBy] as const;
};

export const UserSocialMediaReportingTable = () => {
	const id = useId();
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const [showingSentByModal, setShowingSentByModal] = useState(false);
	// Raw strings, not the enum parser: a rejected value has to stay visible so the effect below can rewrite
	// it out of the URL instead of leaving the link naming a status the report is not applying.
	const [statusFromUrl, setStatus] = useQueryState(
		'status',
		parseAsArrayOf(parseAsString).withDefault(allReportStatusValues as string[])
	);
	const status = useMemo(() => reportStatusOrAll(statusFromUrl), [statusFromUrl]);
	// `withDefault` only covers an absent param, and unknown values are dropped rather than rejected — so
	// rewrite whenever what the report applies differs from what the link asked for.
	useEffect(() => {
		if (statusFromUrl.join() !== status.join()) {
			setStatus(status);
		}
	}, [statusFromUrl, status, setStatus]);
	const [userId, setSentBy] = useSenderFilter();
	const userQuery = useUserQuery({
		enabled: Boolean(userId) && userId !== userSession.user.id,
		id: userId,
	});
	const postStatus = mapReportPostStatusToPostStatus(status);
	let senderLabel = '...';
	if (!userId) {
		senderLabel = 'All Employees';
	} else if (userId === userSession.user.id) {
		senderLabel = 'Me';
	} else if (userQuery.data) {
		senderLabel = getDisplayName(userQuery.data);
	}
	const isCustomFilter = status.length !== allReportStatusValues.length || Boolean(userId);
	const socialMediaPostsQuery = useInfiniteSocialMediaPosts({
		selectedPostStatus: postStatus,
		meta: {
			errorLogging: {
				category: 'SocialMediaReportingTable',
				action: 'GetSocialReports-Error',
			},
		},
		selectedUserId: userId,
	});
	const handleStatusChanged = (value: Api.ReportPostStatus[]) => {
		if (value.length === 0) {
			setStatus(allReportStatusValues);
			return;
		}
		setStatus(value);
	};
	const onClearFilters = () => {
		setStatus(allReportStatusValues);
		setSentBy(null);
	};
	const handleReportTitleClick = (report: Api.SocialMediaPostReportViewModel) => {
		fullscreenModal.history.push({
			pathname:
				report.status === Api.PostStatus.Pending
					? '/social-media/post/approval-preview'
					: `/social-media/post/edit-from-post-report`,
			state: { viewModel: report },
		});
	};

	return (
		<div>
			<div className={css(styleSheet.filter)}>
				{'Filter By:'}
				<Select multiple value={status} onValueChange={handleStatusChanged}>
					<SelectTrigger styles={[bs.w60]}>
						<SelectValue>{status.length === allReportStatusValues.length ? 'All' : status.join(', ')}</SelectValue>
					</SelectTrigger>
					<SelectContent>
						{allReportStatusValues.map(value => {
							return (
								<SelectCheckboxItem key={`${id}-status-${value}`} value={value}>
									{value}
								</SelectCheckboxItem>
							);
						})}
					</SelectContent>
				</Select>
				<Dropdown>
					<DropdownSelectTrigger styles={[bs.w60]}>Sent by {senderLabel}</DropdownSelectTrigger>
					<DropdownContent>
						{userSession.isAdmin ? (
							<DropdownItem
								onClick={() => {
									if (userId != null) {
										setSentBy(null);
									}
								}}
							>
								All Employees
							</DropdownItem>
						) : null}
						<DropdownItem
							onClick={() => {
								if (userId !== userSession.user.id) {
									setSentBy(userSession.user.id);
								}
							}}
						>
							Sent by Me
						</DropdownItem>
						{userSession.isAdmin ? (
							<DropdownItem onClick={() => setShowingSentByModal(true)}>Select an Employee</DropdownItem>
						) : null}
					</DropdownContent>
				</Dropdown>
				{isCustomFilter ? (
					<Button variant='secondary' onClick={onClearFilters}>
						Clear filters
					</Button>
				) : null}
			</div>
			<SocialMediaReportingTable postStatus={postStatus} userId={userId}>
				{socialMediaPostsQuery.status === 'success'
					? socialMediaPostsQuery.data.pages.map(group => {
							const isExplicitlyCancelledFilter = postStatus?.includes(Api.PostStatus.Cancelled);
							const filteredPosts = filterCancelledSocialPosts(group.values, isExplicitlyCancelledFilter);
							return filteredPosts.map(item => {
								const itemVm = new Api.SocialMediaPostReportViewModel(userSession, item);
								return (
									<SocialMediaReportingTableRow key={itemVm.id} report={itemVm} onTitleClick={handleReportTitleClick} />
								);
							});
						})
					: null}
			</SocialMediaReportingTable>
			{socialMediaPostsQuery.hasNextPage && !socialMediaPostsQuery.isFetchingNextPage ? (
				<Waypoint
					bottomOffset='-200px'
					onEnter={() => {
						socialMediaPostsQuery.fetchNextPage();
					}}
				/>
			) : null}
			<UserSelectModal
				modalProps={{
					isOpen: showingSentByModal,
					onRequestClose: (user?: Api.UserViewModel, cancel?: boolean) => {
						if (user && !cancel) {
							setSentBy(user.id);
						}
						setShowingSentByModal(false);
					},
				}}
				title='Select an Employee'
			/>
		</div>
	);
};
